<template>
  <div class="size-toggle">
    <div class="size-toggle__container">
      <div
        v-if="toggleValues?.length && productsByTagState === 'ready'"
        class="size-toggle__wrapper"
      >
        <div class="size-toggle__info">
          <p
            class="size-toggle__title text-body-2xs"
            v-text="$string('product.options.size')"
          />
          <stock-indicator :product="product" />
        </div>

        <div class="size-toggle__content">
          <div
            v-if="toggleValues.length > 1"
            class="size-toggle__toggles"
          >
            <btn
              v-for="(toggle, index) in toggleValues"
              :key="index"
              block-class="size-toggle"
              :show-icon="false"
              :show-state-icon="toggle.value.id === product.id"
              :label="toggle.size"
              :modifiers="['text']"
              type="button"
              @click="updateProductId(toggle)"
            />
          </div>
        </div>
      </div>

      <div v-else-if="productsByTagState === 'ready'">
        <div class="size-toggle__info">
          <p
            class="size-toggle__title text-body-2xs"
            v-text="$string('product.options.size')"
          />
          <stock-indicator :product="product" />
        </div>
      </div>

      <div
        v-else
        class="size-toggle__wrapper is-loading"
      >
        <p
          class="size-toggle__title text-body-2xs"
          v-text="$string('product.options.size')"
        />

        <div
          v-if="!hideSizeToggle"
          class="size-toggle__content"
        >
          <div class="size-toggle__button is-loading" />
          <div class="size-toggle__button is-loading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Size toggle (size-toggle)
 * -----------------------------------------------------------------------------
 * Toggle facility to move between the normal and plus size version of a product.
 *
 * @param {Object} product - Product object.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import StockIndicator from '~async/stock-indicator/stock-indicator'

export default {
  name: 'SizeToggle',

  components: {
    Btn,
    StockIndicator,
  },

  props: {
    hideSizeToggle: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: ['product-id-update', 'product-variant-update'],

  data() {
    return {
      toggleValues: null,
    }
  },

  computed:{

    /**
     * Map Vuex state.
     */
    ...mapState({
      currentSize: (state) => state.product.size,
      colourFamily: (state) => state.product.colourFamily,
      products: (state) => state.product.productsByTag,
      productsByTagState: (state) => state.product.productsByTagState,
      sizeFamily: (state) => state.product.sizeFamily,
    }),
  },

  watch: {

    /**
     * When the product changes build new toggle values.
     */
    products() {
      this.toggleValues = this.constructToggleValues()
    },

    /**
     * When the size family changes build new toggle values.
     */
    sizeFamily() {
      this.toggleValues = this.constructToggleValues()
    },

    /**
     * When the colour changes build new toggle values.
     */
    colourFamily() {
      this.toggleValues = this.constructToggleValues()
    },

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setProduct: 'product/setProduct',
      setVariant: 'product/setVariant',
    }),

    /**
     * Handles emitting the new product id when the handle updates.
     * @param {Object} val - The new product being updated to.
     */
    updateProductId(val) {
      if (!val) {
        return
      }

      if (val.value.handle === cnvs.page.handle) {
        return
      }

      this.$emit('product-variant-update', val)
      this.$emit('product-id-update', val)
    },

    /**
     * Handles the building of new toggle values.
     * @returns {Array, Null}
     */
    constructToggleValues() {
      if (!this.products) {
        return null
      }

      const swatchColour =
        this.product?.metafields?.find(
          metafield => metafield.name === 'swatch_code',
        )

      if (!swatchColour){
        return null
      }

      const sizeProducts =
        this.products.filter(product => {
          const productSwatchCode =
            product.metafields.find(
              metafield => metafield.name === 'swatch_code',
            )

          if (
              product.tags.includes(this.sizeFamily) &&
              productSwatchCode?.value === swatchColour.value
            ){
              return product
          }
        })

      if (!sizeProducts.length) {
        return null
      }

      const toggleValues = sizeProducts.reduce((agg, item) => {
        const size = this.getTagSize(item)

        if (!size) {
          return agg
        }

        if (agg.find(item => item.size === size)) {
          return agg
        }

        agg.push({
          size: size,
          value: item,
        })
        return agg
      }, [])

      toggleValues.sort((a, b) => a.size.localeCompare(b.size)).reverse()
      return toggleValues
    },

    /**
     * Grabs the tag size from the product tags.
     * @param {Object} product - Product object.
     * @returns {String, Boolean}
     */
    getTagSize(product) {
      if (!product?.tags) {
        return false
      }

      const size = product.tags.find(tag => tag.includes('ProductSize-'))
      if (!size) {
        return false
      }

      return size.split('-')[1] || false
    },
  },
}
</script>

<style lang="scss">
@import './size-toggle';
</style>
